import { useTheme } from "../hooks/useTheme";
import { Mail, Linkedin, Github } from "lucide-react";
import { siX } from "simple-icons";
import stoneTexture from "../assets/texture.jpg";
import GraphiteLogo from "./GraphiteLogo";

const Footer = () => {
  const { isDarkMode } = useTheme();

  return (
    <footer
      className={`py-8 sm:pt-10 lg:pt-12 border-t relative overflow-hidden transition-all duration-700 z-50 ${
        isDarkMode
          ? "border-graphite-dark text-white"
          : "border-graphite-silver text-graphite-darkest"
      }`}
    >
      {/* Stone texture background */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(${stoneTexture})`,
          backgroundSize: "cover",
          opacity: isDarkMode ? 0.3 : 0.6,
        }}
      ></div>

      {/* Overlay for text readability */}
      <div
        className="absolute inset-0 z-10"
        style={{
          backgroundColor: isDarkMode
            ? "rgba(26, 26, 26, 0.6)"
            : "rgba(240, 240, 240, 0.2)",
        }}
      ></div>

      <div className="w-full px-4 sm:px-6 md:px-8 lg:px-12 xl:px-16 2xl:px-20 relative z-10">
        {/* Main content area */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8 mb-6">
          {/* Brand section */}
          <div className="lg:col-span-2">
            <div className="mb-2 sm:mb-4">
              <GraphiteLogo className="h-8 sm:h-10 lg:h-12 w-auto" />
            </div>
            <p
              className={`text-sm sm:text-base lg:text-lg leading-relaxed max-w-lg mb-4 transition-colors duration-700 ${
                isDarkMode ? "text-gray-200" : "text-graphite-darkest"
              }`}
            >
              Building the future, one line of code at a time.
            </p>

            {/* Services links */}
            <div className="mb-4">
              <h4
                className={`text-base sm:text-lg font-semibold mb-3 transition-colors duration-700 ${
                  isDarkMode ? "text-white" : "text-graphite-darkest"
                }`}
              >
                Services
              </h4>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                <a
                  href="#services"
                  className={`no-underline transition-all duration-300 ease-in-out text-sm sm:text-base relative inline-block group ${
                    isDarkMode
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-800 hover:text-white"
                  }`}
                >
                  Web Development
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-graphite-medium to-graphite-shine group-hover:w-full transition-all duration-300"></span>
                </a>
                <a
                  href="#services"
                  className={`no-underline transition-all duration-300 ease-in-out text-sm sm:text-base relative inline-block group ${
                    isDarkMode
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-800 hover:text-white"
                  }`}
                >
                  Game Development
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-graphite-medium to-graphite-shine group-hover:w-full transition-all duration-300"></span>
                </a>
                <a
                  href="#services"
                  className={`no-underline transition-all duration-300 ease-in-out text-sm sm:text-base relative inline-block group ${
                    isDarkMode
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-800 hover:text-white"
                  }`}
                >
                  Web3 Solutions
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-graphite-medium to-graphite-shine group-hover:w-full transition-all duration-300"></span>
                </a>
                <a
                  href="#services"
                  className={`no-underline transition-all duration-300 ease-in-out text-sm sm:text-base relative inline-block group ${
                    isDarkMode
                      ? "text-gray-300 hover:text-white"
                      : "text-gray-800 hover:text-white"
                  }`}
                >
                  Mobile Apps
                  <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-gradient-to-r from-graphite-medium to-graphite-shine group-hover:w-full transition-all duration-300"></span>
                </a>
              </div>
            </div>
          </div>

          {/* Connect section with icons */}
          <div className="lg:col-span-1">
            <h4
              className={`text-base sm:text-lg font-semibold mb-4 transition-colors duration-700 ${
                isDarkMode ? "text-white" : "text-graphite-darkest"
              }`}
            >
              Let's Connect
            </h4>
            <div className="flex flex-wrap gap-4 sm:gap-6">
              <a
                href="mailto:info@graphbyte.tech"
                className={`group transition-all duration-300 ease-in-out hover:scale-110 ${
                  isDarkMode
                    ? "text-gray-300 hover:text-white"
                    : "text-graphite-dark hover:text-graphite-darkest"
                }`}
                title="Email"
              >
                <Mail size={24} />
              </a>
              <a
                href="https://www.linkedin.com/in/daniel-gansen-ba64b31ba/"
                target="_blank"
                rel="noopener noreferrer"
                className={`group transition-all duration-300 ease-in-out hover:scale-110 ${
                  isDarkMode
                    ? "text-gray-300 hover:text-white"
                    : "text-graphite-dark hover:text-graphite-darkest"
                }`}
                title="LinkedIn"
              >
                <Linkedin size={24} />
              </a>
              <a
                href="https://github.com/GDansen"
                target="_blank"
                rel="noopener noreferrer"
                className={`group transition-all duration-300 ease-in-out hover:scale-110 ${
                  isDarkMode
                    ? "text-gray-300 hover:text-white"
                    : "text-graphite-dark hover:text-graphite-darkest"
                }`}
                title="GitHub"
              >
                <Github size={24} />
              </a>
              <a
                href="https://x.com/daniel_gansen"
                target="_blank"
                rel="noopener noreferrer"
                className={`group transition-all duration-300 ease-in-out hover:scale-110 ${
                  isDarkMode
                    ? "text-gray-300 hover:text-white"
                    : "text-graphite-dark hover:text-graphite-darkest"
                }`}
                title="Twitter"
              >
                <svg
                  width={24}
                  height={24}
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d={siX.path} />
                </svg>
              </a>
            </div>
          </div>
        </div>

        <div
          className={`border-t pt-3 sm:pt-4 text-center transition-colors duration-700 ${
            isDarkMode ? "border-graphite-dark" : "border-graphite-light"
          }`}
        >
          <p
            className={`m-0 text-sm sm:text-base transition-colors duration-700 ${
              isDarkMode ? "text-gray-300" : "text-graphite-dark"
            }`}
          >
            &copy; 2026 GraphByte OÜ. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
